import { nanoid } from 'nanoid';
import type { Cache } from '@bayer-int/portletce-shared';
import { isNotNullable, Signal } from '@bayer-int/portletce-shared';
import * as t from 'io-ts';

import { Attributes } from 'portlet/common/utils';
import type {
    TelemetryConfig,
    TelemetryConfigCodecType} from 'portlet/services/TelemetryService';
import {
    telemetryDefaultConfig
} from 'portlet/services/TelemetryService';

import type { Setting, SettingValueMap } from './settings';
import { Settings, SettingKeys } from './settings';
import { SpecStatus, Specification } from './specification';

export type Config = {
    pid: string;
    noauth: boolean;
    env: 'dev' | 'prod';
    src?: string;
    namespace: string;
    viewid?: number;
    stylesheet?: string | { css: string };
    nocdn: boolean;
    nocache: boolean;
    local: boolean;
    // unique id for internal purposes
    uniqueid: string;
    settings: Setting[];
    versionstatus?: SpecStatus;
    portletVersion?: number;
    portletNamespace?: string;
    author?: string;
    suppressautodate?: boolean;
    signals: Signal[];
    fetchcache?: Cache;
    disablechartanimations?: boolean;
    telemetry?: TelemetryConfig | false;
    locale: string;
    disableerrormonitoring?: boolean;
    performancethreshold?: number;
};

// in web-component mode, each attribute is a string by default
// but since the values can be also set as a property via js, we should assume unknown
export type RawConfig = { [K in keyof Config]?: unknown };

export const defaults: Config = {
    pid: '',
    noauth: false,
    env: 'prod',
    uniqueid: '',
    namespace: 'roiportlet',
    nocdn: false,
    nocache: false,
    local: false,
    suppressautodate: false,
    versionstatus: SpecStatus.RELEASED,
    settings: [],
    signals: [],
    telemetry: telemetryDefaultConfig,
    locale: 'en-US',
    performancethreshold: 10000,
};

export const Config = {
    defaults,

    parse: (attributes: RawConfig): Config => {
        const nonNullableAttrs: Partial<Config> = {
            pid: Attributes.parseString(attributes.pid),
            noauth: Attributes.parseBool(attributes.noauth),
            env: attributes.env === 'dev' ? 'dev' : 'prod',
            src: Attributes.parseString(attributes.src),
            namespace: Attributes.parseString(attributes.namespace),
            viewid: Attributes.parseInteger(attributes.viewid),
            nocdn: Attributes.parseBool(attributes.nocdn),
            nocache: Attributes.parseBool(attributes.nocache),
            local: Attributes.parseBool(attributes.local),
            uniqueid: Attributes.parseString(attributes.uniqueid) || nanoid(),
            settings: t.array(Settings.keyValueCodec).is(attributes.settings)
                ? attributes.settings
                : undefined,
            versionstatus: Specification.specStatusCodec.is(attributes.versionstatus)
                ? attributes.versionstatus
                : undefined,
            portletVersion: Attributes.parseInteger(attributes.portletVersion),
            portletNamespace: Attributes.parseString(attributes.portletNamespace),
            author: Attributes.parseString(attributes.author),
            suppressautodate: Attributes.parseBool(attributes.suppressautodate),
            signals: t.array(Signal.codec).is(attributes.signals) ? attributes.signals : undefined,
            fetchcache: attributes.fetchcache instanceof Map ? attributes.fetchcache : undefined,
            disablechartanimations: Attributes.parseBool(attributes.disablechartanimations),
            telemetry: t.record(t.string, t.unknown).is(attributes.telemetry)
                ? { ...defaults.telemetry, ...(attributes.telemetry as TelemetryConfigCodecType) }
                : false,
            locale: Attributes.parseString(attributes.locale),
            disableerrormonitoring: Attributes.parseBool(attributes.disableerrormonitoring),
            performancethreshold: Attributes.parseNumber(attributes.performancethreshold),
        };
        return {
            ...defaults,
            ...Attributes.omitUndefinedKeys(nonNullableAttrs),
        };
    },

    toSettings: (config: Config): Partial<SettingValueMap> => {
        return Settings.merge(
            isNotNullable(config.viewid) && {
                [SettingKeys.VIEWID]: config.viewid,
            },
            Settings.fromSettingList(config.settings),
        );
    },
};
